import { AgentMentionKind, AgentMentionOption } from './agent-mention.model';

// Caps sites and audiences so a long list of either can't push matching campaigns out of view.
const KIND_SHARE: Partial<Record<AgentMentionKind, number>> = { site: 3, audience: 3 };

interface RankedMention {
  option: AgentMentionOption;
  rank: number;
  order: number;
}

export function rankMentions(
  options: readonly AgentMentionOption[],
  query: string,
  limit: number,
): AgentMentionOption[] {
  const needle = query.trim().toLowerCase();
  const terms = needle.split(/\s+/).filter((term) => term.length > 0);
  const ranked: RankedMention[] = [];
  options.forEach((option, order) => {
    const rank = matchRank(option, needle, terms);
    if (rank !== undefined) {
      ranked.push({ option, rank, order });
    }
  });
  ranked.sort(byRank);

  let reserved = 0;
  for (const [kind, share] of Object.entries(KIND_SHARE)) {
    reserved += Math.min(share, ranked.filter((entry) => entry.option.kind === kind).length);
  }
  const picked: RankedMention[] = [];
  const leftover: RankedMention[] = [];
  const pickedPerKind = new Map<AgentMentionKind, number>();
  for (const entry of ranked) {
    const kind = entry.option.kind;
    const count = pickedPerKind.get(kind) ?? 0;
    if (count < (KIND_SHARE[kind] ?? limit - reserved)) {
      picked.push(entry);
      pickedPerKind.set(kind, count + 1);
    } else {
      leftover.push(entry);
    }
  }
  picked.push(...leftover.slice(0, limit - picked.length));
  return picked.sort(byRank).map((entry) => entry.option);
}

function matchRank(option: AgentMentionOption, needle: string, terms: string[]): number | undefined {
  if (option.searchText === needle || String(option.id).toLowerCase() === needle) {
    return 0;
  }
  if (option.searchText.startsWith(needle)) {
    return 1;
  }
  const words = option.searchText.split(/[^\p{L}\p{N}]+/u);
  if (terms.every((term) => words.some((word) => word.startsWith(term)))) {
    return 2;
  }
  if (terms.every((term) => option.searchText.includes(term))) {
    return 3;
  }
  return undefined;
}

function byRank(a: RankedMention, b: RankedMention): number {
  return a.rank - b.rank || a.order - b.order;
}
